// ────────────────────────────────────────────────────────────────────────────────
// DDC Construction Skills — TypeScript port for BuildPro
// Source: github.com/datadrivenconstruction/DDC_Skills_for_AI_Agents_in_Construction
// License: MIT
//
// Combines 3 CWICR skills into one service:
// 1. Takeoff Helper — quantity calculations with waste factors + unit conversions
// 2. Cost Calculator — resource-based pricing with MO/materiales/equipos breakdown
// 3. Value Engineering — find cheaper alternatives, identify high-cost items
// ────────────────────────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────────────────────

export interface CostBreakdown {
  laborCost: number;
  materialCost: number;
  equipmentCost: number;
  directCost: number;
  overheadCost: number;
  profitCost: number;
  totalCost: number;
  unitPrice: number;
}

export interface TakeoffItem {
  code: string;
  description: string;
  unit: string;
  grossQuantity: number;
  wasteFactor: number;
  netQuantity: number;
  calculation: string;
}

export interface WasteDetail {
  materialType: string;
  cuttingPct: number;
  spillagePct: number;
  breakagePct: number;
  overrunPct: number;
  totalWastePct: number;
  baseQuantity: number;
  orderQuantity: number;
  wasteQuantity: number;
  wasteCost: number;
}

export interface VEAlternative {
  originalLabel: string;
  alternativeLabel: string;
  originalPrice: number;
  alternativePrice: number;
  savings: number;
  savingsPct: number;
  category: string;
}

// ── Waste Factors by Material (industry standard, Spain) ─────────────────────

const WASTE_FACTORS: Record<string, { cutting: number; spillage: number; breakage: number; overrun: number }> = {
  hormigón:    { cutting: 0.02, spillage: 0.03, breakage: 0.00, overrun: 0.02 },
  armadura:    { cutting: 0.05, spillage: 0.00, breakage: 0.01, overrun: 0.02 },
  ladrillo:    { cutting: 0.05, spillage: 0.00, breakage: 0.03, overrun: 0.02 },
  bloque:      { cutting: 0.04, spillage: 0.00, breakage: 0.02, overrun: 0.02 },
  madera:      { cutting: 0.10, spillage: 0.00, breakage: 0.02, overrun: 0.03 },
  tabique:     { cutting: 0.10, spillage: 0.00, breakage: 0.03, overrun: 0.02 },
  pladur:      { cutting: 0.10, spillage: 0.00, breakage: 0.03, overrun: 0.02 },
  azulejo:     { cutting: 0.10, spillage: 0.00, breakage: 0.05, overrun: 0.03 },
  gres:        { cutting: 0.10, spillage: 0.00, breakage: 0.05, overrun: 0.03 },
  baldosa:     { cutting: 0.10, spillage: 0.00, breakage: 0.05, overrun: 0.03 },
  pintura:     { cutting: 0.00, spillage: 0.05, breakage: 0.00, overrun: 0.10 },
  mortero:     { cutting: 0.00, spillage: 0.05, breakage: 0.00, overrun: 0.03 },
  aislamiento: { cutting: 0.08, spillage: 0.00, breakage: 0.02, overrun: 0.03 },
  cubierta:    { cutting: 0.10, spillage: 0.00, breakage: 0.02, overrun: 0.05 },
  tubería:     { cutting: 0.05, spillage: 0.00, breakage: 0.01, overrun: 0.02 },
  cableado:    { cutting: 0.03, spillage: 0.00, breakage: 0.00, overrun: 0.05 },
  acero:       { cutting: 0.03, spillage: 0.00, breakage: 0.00, overrun: 0.02 },
  vidrio:      { cutting: 0.05, spillage: 0.00, breakage: 0.05, overrun: 0.02 },
  pavimento:   { cutting: 0.10, spillage: 0.00, breakage: 0.02, overrun: 0.03 },
  adhesivo:    { cutting: 0.00, spillage: 0.08, breakage: 0.00, overrun: 0.05 },
  yeso:        { cutting: 0.00, spillage: 0.05, breakage: 0.00, overrun: 0.05 },
  default:     { cutting: 0.05, spillage: 0.02, breakage: 0.02, overrun: 0.03 },
};

// ── Related Work Items ───────────────────────────────────────────────────────

const RELATED_ITEMS: Record<string, string[]> = {
  hormigón:    ['encofrado', 'armadura', 'curado', 'acabado superficial'],
  albañilería: ['mortero', 'armadura', 'dintel', 'enfoscado'],
  tabiquería:  ['estructura', 'aislamiento', 'enlucido', 'pintura'],
  cubierta:    ['impermeabilización', 'canalón', 'ventilación', 'aislamiento'],
  pavimento:   ['nivelación', 'adhesivo', 'rodapié', 'juntas'],
  fontanería:  ['grifería', 'sanitarios', 'desagüe', 'llave de paso'],
  electricidad:['mecanismos', 'cuadro eléctrico', 'toma de tierra', 'canalización'],
};

// ── Unit Conversions ─────────────────────────────────────────────────────────

const CONVERSIONS: Record<string, number> = {
  'm_ft': 3.28084, 'ft_m': 0.3048,
  'm2_sf': 10.7639, 'sf_m2': 0.0929,
  'm3_cf': 35.3147, 'cf_m3': 0.0283,
  'm3_cy': 1.30795, 'cy_m3': 0.7646,
  'kg_lb': 2.20462, 'lb_kg': 0.453592,
};

// ── Takeoff Helper ───────────────────────────────────────────────────────────

/** Convert between units */
export function convertUnit(value: number, from: string, to: string): number {
  if (from === to) return value;
  const key = `${from}_${to}`;
  if (CONVERSIONS[key]) return value * CONVERSIONS[key];
  const rev = `${to}_${from}`;
  if (CONVERSIONS[rev]) return value / CONVERSIONS[rev];
  return value;
}

/** Get waste factor for a material description */
export function detectWasteFactor(description: string): { type: string; factors: typeof WASTE_FACTORS['default'] } {
  const lower = description.toLowerCase();
  for (const [type, factors] of Object.entries(WASTE_FACTORS)) {
    if (type !== 'default' && lower.includes(type)) return { type, factors };
  }
  return { type: 'default', factors: WASTE_FACTORS.default };
}

/** Calculate area with deductions (windows, doors) */
export function calculateArea(
  length: number,
  width: number,
  deductions: Array<[number, number]> = []
): { grossArea: number; deductionArea: number; netArea: number; calculation: string } {
  const grossArea = length * width;
  const deductionArea = deductions.reduce((sum, [l, w]) => sum + l * w, 0);
  const netArea = grossArea - deductionArea;
  return {
    grossArea: Math.round(grossArea * 100) / 100,
    deductionArea: Math.round(deductionArea * 100) / 100,
    netArea: Math.round(netArea * 100) / 100,
    calculation: `${length} × ${width} = ${grossArea.toFixed(2)} m² − ${deductionArea.toFixed(2)} huecos`,
  };
}

/** Calculate volume */
export function calculateVolume(length: number, width: number, depth: number) {
  const vol = length * width * depth;
  return {
    volume: Math.round(vol * 1000) / 1000,
    calculation: `${length} × ${width} × ${depth} = ${vol.toFixed(3)} m³`,
  };
}

/** Full room takeoff — floor, ceiling, walls, with deductions */
export function roomTakeoff(
  length: number,
  width: number,
  height: number,
  openings: Array<[number, number]> = []
): TakeoffItem[] {
  const floorArea = length * width;
  const perim = 2 * (length + width);
  const wallGross = perim * height;
  const openingArea = openings.reduce((s, [w, h]) => s + w * h, 0);
  const wallNet = wallGross - openingArea;

  const floorWaste = WASTE_FACTORS.pavimento;
  const wallWaste = WASTE_FACTORS.pintura;

  const floorTotal = (floorWaste.cutting + floorWaste.spillage + floorWaste.breakage + floorWaste.overrun);
  const wallTotal = (wallWaste.cutting + wallWaste.spillage + wallWaste.breakage + wallWaste.overrun);

  return [
    {
      code: 'SUELO',
      description: 'Pavimento',
      unit: 'm²',
      grossQuantity: Math.round(floorArea * 100) / 100,
      wasteFactor: floorTotal,
      netQuantity: Math.round(floorArea * (1 + floorTotal) * 100) / 100,
      calculation: `${length} × ${width} = ${floorArea.toFixed(2)} m² + ${(floorTotal * 100).toFixed(0)}% merma`,
    },
    {
      code: 'TECHO',
      description: 'Techo',
      unit: 'm²',
      grossQuantity: Math.round(floorArea * 100) / 100,
      wasteFactor: 0.05,
      netQuantity: Math.round(floorArea * 1.05 * 100) / 100,
      calculation: `Igual que suelo: ${floorArea.toFixed(2)} m² + 5% merma`,
    },
    {
      code: 'PAREDES',
      description: 'Paramentos verticales',
      unit: 'm²',
      grossQuantity: Math.round(wallGross * 100) / 100,
      wasteFactor: wallTotal,
      netQuantity: Math.round(wallNet * (1 + wallTotal) * 100) / 100,
      calculation: `${perim.toFixed(1)}m × ${height}m = ${wallGross.toFixed(2)} m² − ${openingArea.toFixed(2)} huecos + ${(wallTotal * 100).toFixed(0)}% merma`,
    },
  ];
}

// ── Cost Calculator ──────────────────────────────────────────────────────────

const DEFAULT_OVERHEAD_RATE = 0.13; // 13% gastos generales (Spain standard)
const DEFAULT_PROFIT_RATE = 0.06;   // 6% beneficio industrial (Spain standard)

/** Calculate cost breakdown for a single item */
export function calculateCostBreakdown(
  laborUnit: number,
  materialUnit: number,
  equipmentUnit: number,
  quantity: number,
  overheadRate: number = DEFAULT_OVERHEAD_RATE,
  profitRate: number = DEFAULT_PROFIT_RATE
): CostBreakdown {
  const laborCost = laborUnit * quantity;
  const materialCost = materialUnit * quantity;
  const equipmentCost = equipmentUnit * quantity;
  const directCost = laborCost + materialCost + equipmentCost;
  const overheadCost = directCost * overheadRate;
  const profitCost = (directCost + overheadCost) * profitRate;
  const totalCost = directCost + overheadCost + profitCost;
  const unitPrice = quantity > 0 ? totalCost / quantity : 0;

  return {
    laborCost: Math.round(laborCost * 100) / 100,
    materialCost: Math.round(materialCost * 100) / 100,
    equipmentCost: Math.round(equipmentCost * 100) / 100,
    directCost: Math.round(directCost * 100) / 100,
    overheadCost: Math.round(overheadCost * 100) / 100,
    profitCost: Math.round(profitCost * 100) / 100,
    totalCost: Math.round(totalCost * 100) / 100,
    unitPrice: Math.round(unitPrice * 100) / 100,
  };
}

/** Calculate project-level cost summary from budget items */
export function calculateProjectSummary(
  items: Array<{ name: string; quantity: number; unit: string; price: number; total: number }>,
  overheadRate: number = DEFAULT_OVERHEAD_RATE,
  profitRate: number = DEFAULT_PROFIT_RATE
): {
  pem: number;
  gg: number;
  bi: number;
  totalPEC: number;
  iva: number;
  totalConIva: number;
  laborPct: number;
  materialPct: number;
} {
  const pem = items.reduce((s, i) => s + i.total, 0);
  const gg = pem * overheadRate;
  const bi = (pem + gg) * profitRate;
  const totalPEC = pem + gg + bi;
  const iva = totalPEC * 0.21; // IVA 21%
  const totalConIva = totalPEC + iva;

  // Estimate labour vs material split (typical Spanish construction: ~40% MO, ~45% MAT, ~15% EQ)
  return {
    pem: Math.round(pem * 100) / 100,
    gg: Math.round(gg * 100) / 100,
    bi: Math.round(bi * 100) / 100,
    totalPEC: Math.round(totalPEC * 100) / 100,
    iva: Math.round(iva * 100) / 100,
    totalConIva: Math.round(totalConIva * 100) / 100,
    laborPct: 40,
    materialPct: 45,
  };
}

// ── Waste Calculator ─────────────────────────────────────────────────────────

/** Calculate detailed waste for a material */
export function calculateWaste(
  description: string,
  baseQuantity: number,
  unitCost: number = 0
): WasteDetail {
  const { type, factors } = detectWasteFactor(description);
  const totalPct = factors.cutting + factors.spillage + factors.breakage + factors.overrun;
  const wasteQty = baseQuantity * totalPct;
  const orderQty = baseQuantity + wasteQty;

  return {
    materialType: type,
    cuttingPct: Math.round(factors.cutting * 1000) / 10,
    spillagePct: Math.round(factors.spillage * 1000) / 10,
    breakagePct: Math.round(factors.breakage * 1000) / 10,
    overrunPct: Math.round(factors.overrun * 1000) / 10,
    totalWastePct: Math.round(totalPct * 1000) / 10,
    baseQuantity,
    orderQuantity: Math.round(orderQty * 100) / 100,
    wasteQuantity: Math.round(wasteQty * 100) / 100,
    wasteCost: Math.round(wasteQty * unitCost * 100) / 100,
  };
}

/** Calculate total waste for a project budget */
export function calculateProjectWaste(
  items: Array<{ name: string; quantity: number; price: number }>
): { items: WasteDetail[]; totalWasteCost: number; avgWastePct: number } {
  const details = items.map(i => calculateWaste(i.name, i.quantity, i.price));
  const totalBase = items.reduce((s, i) => s + i.quantity * i.price, 0);
  const totalWaste = details.reduce((s, d) => s + d.wasteCost, 0);
  return {
    items: details,
    totalWasteCost: Math.round(totalWaste * 100) / 100,
    avgWastePct: totalBase > 0 ? Math.round(totalWaste / totalBase * 1000) / 10 : 0,
  };
}

// ── Value Engineering ────────────────────────────────────────────────────────

/** Suggest related items that should be included alongside a work item */
export function suggestRelatedItems(description: string): string[] {
  const lower = description.toLowerCase();
  for (const [key, items] of Object.entries(RELATED_ITEMS)) {
    if (lower.includes(key)) return items;
  }
  return [];
}

/** Identify the most expensive items in a budget (VE targets) */
export function identifyVETargets(
  items: Array<{ name: string; quantity: number; price: number; total: number }>,
  topN: number = 10,
  minPct: number = 2.0
): Array<{ name: string; total: number; pct: number; veOpportunity: string }> {
  const totalCost = items.reduce((s, i) => s + i.total, 0);
  if (totalCost <= 0) return [];

  return items
    .map(i => ({
      name: i.name,
      total: i.total,
      pct: Math.round(i.total / totalCost * 1000) / 10,
      veOpportunity: i.total / totalCost > 0.10
        ? '🔴 Alta — revisar alternativas'
        : i.total / totalCost > 0.05
          ? '🟡 Media — considerar alternativas'
          : '🟢 Baja',
    }))
    .filter(i => i.pct >= minPct)
    .sort((a, b) => b.total - a.total)
    .slice(0, topN);
}

/** Generate a basic VE summary text */
export function generateVESummary(
  items: Array<{ name: string; quantity: number; price: number; total: number }>
): string {
  const targets = identifyVETargets(items, 5);
  const totalCost = items.reduce((s, i) => s + i.total, 0);
  const wasteData = calculateProjectWaste(items.map(i => ({ name: i.name, quantity: i.quantity, price: i.price })));

  let summary = `## Análisis de Ingeniería de Valor\n\n`;
  summary += `**PEM Total**: ${totalCost.toLocaleString('es-ES')}€\n`;
  summary += `**Coste de mermas estimado**: ${wasteData.totalWasteCost.toLocaleString('es-ES')}€ (${wasteData.avgWastePct}%)\n\n`;

  if (targets.length > 0) {
    summary += `### Partidas objetivo para VE\n\n`;
    summary += `| Partida | Coste | % | Oportunidad |\n|---------|-------|---|-------------|\n`;
    for (const t of targets) {
      summary += `| ${t.name} | ${t.total.toLocaleString('es-ES')}€ | ${t.pct}% | ${t.veOpportunity} |\n`;
    }
  }

  return summary;
}
